"use client";

import PageHeader from "@/components/shared/PageHeader";
import { Suspense, useState } from "react";
import { useRouter } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { getPublicJobs } from "@/lib/api/jobs";
import JobFilters from "@/components/jobs/JobFilters";
import JobList from "@/components/jobs/JobList";
import GlassPanel from "@/components/dashboard/GlassPanel";
import { useJobs } from "@/hooks/useJobs";
import { useAuthStore } from "@/store/authStore";
import Footer from "@/components/layout/Footer";
import type { JobPost } from "@/lib/types/models";
import { useJobFilterUrlState } from "@/components/jobs/useJobFilterUrlState";

const PAGE_SIZE = 20;

export default function PublicJobsPage() {
  return (
    <Suspense fallback={<PublicJobsFallback />}>
      <PublicJobsPageContent />
    </Suspense>
  );
}

function PublicJobsPageContent() {
  const router = useRouter();
  const [savedJobIds, setSavedJobIds] = useState<string[]>([]);
  const { isAuthenticated } = useAuthStore();
  const { saveJob, unsaveJob } = useJobs();
  const {
    jobType,
    category,
    location,
    debouncedLocation,
    rankId,
    vesselTypeId,
    shorePositionId,
    page,
    setJobType,
    setCategory,
    setLocation,
    setRankId,
    setVesselTypeId,
    setShorePositionId,
    setPage,
  } = useJobFilterUrlState(PAGE_SIZE);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      "public-jobs",
      jobType,
      category,
      rankId,
      vesselTypeId,
      shorePositionId,
      debouncedLocation,
      page,
    ],
    queryFn: ({ signal }) =>
      getPublicJobs({
        type: jobType,
        ...(jobType === "onboard"
          ? { rankId: rankId || undefined, vesselTypeId: vesselTypeId || undefined }
          : {
              shorePositionId: shorePositionId || undefined,
              category: category === "training" ? category : undefined,
            }),
        location: debouncedLocation || undefined,
        page,
        pageSize: PAGE_SIZE,
      }, signal),
    staleTime: 1000 * 60 * 2,
  });

  const jobs = (data?.results ?? []) as JobPost[];
  const totalPages = data?.totalPages ?? 1;

  const toggleSaveJob = async (id: string) => {
    if (!isAuthenticated) {
      router.push(`/login?redirect=/jobs`);
      return;
    }
    if (savedJobIds.includes(id)) {
      await unsaveJob(id);
      setSavedJobIds((prev) => prev.filter((j) => j !== id));
    } else {
      await saveJob(id);
      setSavedJobIds((prev) => [...prev, id]);
    }
  };

  return (
    <>
      <div className="space-y-6">
        <PageHeader
          title="Browse Jobs"
          subtitle="Explore all published onboard and shore positions"
        />

        <GlassPanel className="p-5">
          <JobFilters
            jobType={jobType}
            onJobTypeChange={setJobType}
            category={category}
            onCategoryChange={setCategory}
            location={location}
            onLocationChange={setLocation}
            rankId={rankId}
            onRankIdChange={setRankId}
            vesselTypeId={vesselTypeId}
            onVesselTypeIdChange={setVesselTypeId}
            shorePositionId={shorePositionId}
            onShorePositionIdChange={setShorePositionId}
          />
        </GlassPanel>

        <JobList
          jobs={jobs}
          isLoading={isLoading}
          error={error as Error | null}
          onRefetch={refetch}
          count={data?.count ?? 0}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          savedJobIds={savedJobIds}
          onSaveToggle={toggleSaveJob}
          onApply={(id) => router.push(`/jobs/${id}`)}
        />
      </div>
      <Footer />
    </>
  );
}

function PublicJobsFallback() {
  return (
    <>
      <div className="space-y-6">
        <PageHeader
          title="Browse Jobs"
          subtitle="Explore all published onboard and shore positions"
        />
        <GlassPanel className="p-5">
          <div className="h-24 animate-pulse rounded-lg bg-background motion-reduce:animate-none" />
        </GlassPanel>
      </div>
      <Footer />
    </>
  );
}
